import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import styles from './TextColorMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { useRecentColors } from '../../hooks/useRecentColors';
import { ColorPickerPanel } from '../ColorPickerPanel/ColorPickerPanel';
import { applyMarkFast } from '../applyMarkFast';

// Google-Docs-style palette: greyscale + bright row, then 6 tint/shade rows,
// 10 columns each.
const PALETTE = [
    '#000000',
    '#434343',
    '#666666',
    '#999999',
    '#b7b7b7',
    '#cccccc',
    '#d9d9d9',
    '#efefef',
    '#f3f3f3',
    '#ffffff',
    '#980000',
    '#ff0000',
    '#ff9900',
    '#ffff00',
    '#00ff00',
    '#00ffff',
    '#4a86e8',
    '#0000ff',
    '#9900ff',
    '#ff00ff',
    '#e6b8af',
    '#f4cccc',
    '#fce5cd',
    '#fff2cc',
    '#d9ead3',
    '#d0e0e3',
    '#c9daf8',
    '#cfe2f3',
    '#d9d2e9',
    '#ead1dc',
    '#dd7e6b',
    '#ea9999',
    '#f9cb9c',
    '#ffe599',
    '#b6d7a8',
    '#a2c4c9',
    '#a4c2f4',
    '#9fc5e8',
    '#b4a7d6',
    '#d5a6bd',
    '#cc4125',
    '#e06666',
    '#f6b26b',
    '#ffd966',
    '#93c47d',
    '#76a5af',
    '#6d9eeb',
    '#6fa8dc',
    '#8e7cc3',
    '#c27ba0',
    '#a61c00',
    '#cc0000',
    '#e69138',
    '#f1c232',
    '#6aa84f',
    '#45818e',
    '#3c78d8',
    '#3d85c6',
    '#674ea7',
    '#a64d79',
    '#85200c',
    '#990000',
    '#b45f06',
    '#bf9000',
    '#38761d',
    '#134f5c',
    '#1155cc',
    '#0b5394',
    '#351c75',
    '#741b47',
    '#5b0f00',
    '#660000',
    '#783f04',
    '#7f6000',
    '#274e13',
    '#0c343d',
    '#1c4587',
    '#073763',
    '#20124d',
    '#4c1130',
];

export function TextColorMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();
    const [recents, pushRecent] = useRecentColors('plateEditor.recentColors.text');

    const currentColor: string | undefined = useEditorSelector((ed: any) => ed?.api?.marks?.()?.color, []);

    const applyColor = (hex: string) => {
        if (!hex) return;
        const ed: any = editor;
        // Single-pass apply — see applyMarkFast for the rationale.
        applyMarkFast(ed, 'color', hex, (v) => ed?.tf?.color?.addMark?.(v));
        setOpen(false);
    };

    const clearColor = () => {
        (editor as any).tf?.removeMarks?.('color');
        setOpen(false);
    };

    const swatchColor = currentColor || '#37352f';

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                title="Text color"
                aria-label="Text color"
                aria-haspopup="true"
                aria-expanded={open}
                style={{ color: swatchColor }}
            >
                <FormatColorTextIcon fontSize="small" />
            </button>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Pick text color" onMouseDown={(e) => e.preventDefault()}>
                    <ColorPickerPanel
                        value={currentColor}
                        previewColor={swatchColor}
                        palette={PALETTE}
                        recents={recents}
                        onPick={applyColor}
                        pushRecent={pushRecent}
                        onClear={clearColor}
                        clearLabel="Default"
                    />
                </div>
            )}
        </div>
    );
}
